// Epic Classification — AI-assisted review panel for the Work Mix tab.
//
// Pick a sprint, hit "Suggest with AI", and Claude reads each epic's description
// and proposes one of the four epic Work-Mix buckets with a confidence score and
// a one-line rationale. The reviewer approves the suggestion (or overrides it).
// Approved categories are intrinsic to the epic and override the summary-regex
// classifier everywhere that epic appears — so refresh the dashboard after
// approving to see the Work Mix numbers move.

import { useCallback, useMemo, useState } from 'react'
import { PULSE_COLORS } from './pulseConfig.js'

// Mirror the Work Mix CATEGORY_META (minus 'bug', which stays issue-level).
const EPIC_CATEGORIES = ['feature', 'support', 'tech_debt', 'other']
const CATEGORY_META = {
    feature: { label: 'Feature', color: '#3b82f6', icon: '🔷' },
    support: { label: 'Support', color: '#f59e0b', icon: '🛠️' },
    tech_debt: { label: 'Tech Debt', color: '#a78bfa', icon: '🔧' },
    other: { label: 'Other', color: '#64748b', icon: '📦' },
}

function confColor(label) {
    if (label === 'high') return PULSE_COLORS.success
    if (label === 'medium') return PULSE_COLORS.warning
    return PULSE_COLORS.danger
}

export default function EpicClassification({ defaultSprintNum, onApproved }) {
    const [sprintNum, setSprintNum] = useState(defaultSprintNum ? String(defaultSprintNum) : '')
    const [persist, setPersist] = useState(true)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [data, setData] = useState(null)
    const [rows, setRows] = useState([]) // local mutable copy of data.epics
    const [filter, setFilter] = useState('needs-review') // needs-review | all | approved
    const [savingKey, setSavingKey] = useState(null)
    const [autoApproving, setAutoApproving] = useState(false)
    const [autoMsg, setAutoMsg] = useState(null)

    const run = useCallback(async () => {
        const num = Number(sprintNum)
        if (!Number.isFinite(num) || num < 1) {
            setError('Enter a sprint number (≥ 1)')
            return
        }
        setLoading(true)
        setError(null)
        setData(null)
        setRows([])
        try {
            const url = `/api/pulse/classify-epic/sprint?sprintNum=${num}${persist ? '' : '&dryRun=1'}`
            const r = await fetch(url, { method: 'POST' })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `classify ${r.status}`)
            setData(j)
            setRows(j.epics || [])
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [sprintNum, persist])

    const approve = useCallback(async (epicKey, category) => {
        setSavingKey(epicKey)
        try {
            const r = await fetch('/api/pulse/classify-epic/approve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ epicKey, category }),
            })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `approve ${r.status}`)
            setRows((prev) =>
                prev.map((e) =>
                    e.epic_key === epicKey
                        ? { ...e, status: 'approved', category, approved_by: j.approved_by }
                        : e
                )
            )
            if (onApproved) onApproved()
        } catch (e) {
            alert(`Failed to approve: ${e.message}`)
        } finally {
            setSavingKey(null)
        }
    }, [onApproved])

    const autoApprove = useCallback(async () => {
        if (!window.confirm(
            'Auto-approve EVERY AI-suggested epic with ≥80% confidence, across all sprints? ' +
            "Each takes the model's suggested category. You can still override any of them later."
        )) return
        setAutoApproving(true)
        setAutoMsg(null)
        try {
            const r = await fetch('/api/pulse/classify-epic/auto-approve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ threshold: 0.8 }),
            })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `auto-approve ${r.status}`)
            const approvedKeys = new Set(j.keys || [])
            // Reflect the global change in the currently-loaded rows.
            setRows((prev) =>
                prev.map((e) =>
                    approvedKeys.has(e.epic_key)
                        ? { ...e, status: 'approved', category: e.ai_suggested }
                        : e
                )
            )
            setAutoMsg(`Auto-approved ${j.approved} epic${j.approved === 1 ? '' : 's'} ≥80%`)
            if (onApproved) onApproved()
        } catch (e) {
            alert(`Auto-approve failed: ${e.message}`)
        } finally {
            setAutoApproving(false)
        }
    }, [onApproved])

    const counts = useMemo(() => {
        const c = { all: rows.length, approved: 0, 'needs-review': 0 }
        for (const e of rows) {
            if (e.status === 'approved') c.approved++
            else c['needs-review']++
        }
        return c
    }, [rows])

    const visible = useMemo(() => {
        if (filter === 'all') return rows
        if (filter === 'approved') return rows.filter((e) => e.status === 'approved')
        return rows.filter((e) => e.status !== 'approved')
    }, [rows, filter])

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', maxWidth: '820px', margin: 0 }}>
                Claude reads each epic <strong style={{ color: PULSE_COLORS.text }}>description</strong> and
                proposes a Work-Mix category with a confidence score. Approve the suggestion or override it —
                approved categories override the summary-regex classifier for that epic in every sprint.
                <strong style={{ color: PULSE_COLORS.text }}> Refresh the dashboard after approving</strong> to
                see the Work Mix numbers update.
            </p>

            {/* Controls */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', padding: '12px', background: PULSE_COLORS.cardBg, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '8px' }}>
                <label style={{ color: PULSE_COLORS.text, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Sprint #
                    <input
                        type="number"
                        value={sprintNum}
                        onChange={(e) => setSprintNum(e.target.value)}
                        placeholder="e.g. 16"
                        style={{ width: '80px', background: PULSE_COLORS.bgInner, color: PULSE_COLORS.text, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '4px', padding: '6px 8px', fontSize: '13px' }}
                    />
                </label>
                <label style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="checkbox" checked={persist} onChange={(e) => setPersist(e.target.checked)} />
                    Persist suggestions (uncheck for dry-run)
                </label>
                <button
                    onClick={run}
                    disabled={loading}
                    style={{
                        background: PULSE_COLORS.primary, color: PULSE_COLORS.text, border: 'none',
                        borderRadius: '4px', padding: '8px 16px', fontSize: '13px', fontWeight: 600,
                        cursor: loading ? 'wait' : 'pointer',
                    }}
                >
                    {loading ? 'Asking Claude…' : '✨ Suggest with AI'}
                </button>
                <button
                    onClick={autoApprove}
                    disabled={autoApproving}
                    title="Approve all AI suggestions with ≥80% confidence, across every sprint"
                    style={{
                        background: 'transparent', color: PULSE_COLORS.success,
                        border: `1px solid ${PULSE_COLORS.success}`, borderRadius: '4px',
                        padding: '8px 14px', fontSize: '13px', fontWeight: 600,
                        cursor: autoApproving ? 'wait' : 'pointer',
                    }}
                >
                    {autoApproving ? 'Approving…' : '⚡ Auto-approve ≥80%'}
                </button>
                {autoMsg && <span style={{ color: PULSE_COLORS.success, fontSize: '12px' }}>{autoMsg}</span>}
                {error && <span style={{ color: PULSE_COLORS.danger, fontSize: '12px' }}>{error}</span>}
                {data && (
                    <span style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', marginLeft: 'auto' }}>
                        {data.model} · {data.total} epics · {data.classified_now} new · {data.reused} reused
                        {data.ai_errors ? ` · ${data.ai_errors} errors` : ''}
                    </span>
                )}
            </div>

            {/* Filter tabs */}
            {data && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px' }}>
                    {['needs-review', 'approved', 'all'].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            style={{
                                background: filter === f ? `${PULSE_COLORS.primary}30` : 'transparent',
                                color: filter === f ? PULSE_COLORS.text : PULSE_COLORS.textMuted,
                                border: `1px solid ${filter === f ? PULSE_COLORS.primary : PULSE_COLORS.border}`,
                                borderRadius: '999px', padding: '3px 12px', cursor: 'pointer',
                            }}
                        >
                            {f === 'needs-review' ? 'Needs review' : f === 'approved' ? 'Approved' : 'All'} ({counts[f]})
                        </button>
                    ))}
                </div>
            )}

            {/* Epic rows */}
            {data && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {visible.length === 0 && (
                        <span style={{ color: PULSE_COLORS.textSubtle, fontSize: '13px' }}>
                            Nothing here — try another filter.
                        </span>
                    )}
                    {visible.map((epic) => (
                        <EpicRow
                            key={epic.epic_key}
                            epic={epic}
                            saving={savingKey === epic.epic_key}
                            onApprove={approve}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function EpicRow({ epic, saving, onApprove }) {
    // Local choice starts at the AI suggestion (or the already-approved category).
    const [choice, setChoice] = useState(epic.category || epic.ai_suggested || 'feature')
    const [expanded, setExpanded] = useState(false)
    const approved = epic.status === 'approved'
    const sugMeta = CATEGORY_META[epic.ai_suggested] || CATEGORY_META.other
    const effMeta = CATEGORY_META[epic.category] || CATEGORY_META.other
    const jiraUrl = `https://bstock.atlassian.net/browse/${epic.epic_key}`
    const confPct = epic.confidence != null ? Math.round(epic.confidence * 100) : null
    const overriding = choice !== epic.ai_suggested

    return (
        <div
            style={{
                padding: '12px',
                background: PULSE_COLORS.cardBg,
                border: `1px solid ${approved ? PULSE_COLORS.success : PULSE_COLORS.border}`,
                borderLeft: `4px solid ${approved ? PULSE_COLORS.success : effMeta.color}`,
                borderRadius: '6px',
                fontSize: '13px',
            }}
        >
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <a href={jiraUrl} target="_blank" rel="noreferrer" style={{ color: PULSE_COLORS.primary, fontFamily: 'monospace', fontSize: '12px', textDecoration: 'none', minWidth: '88px', paddingTop: '2px' }}>
                    {epic.epic_key}
                </a>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: PULSE_COLORS.text, fontWeight: 600, marginBottom: '4px' }}>
                        {epic.summary || '(no summary)'}
                    </div>
                    {epic.description_excerpt ? (
                        <div
                            onClick={() => setExpanded((v) => !v)}
                            title="Click to expand"
                            style={{
                                color: PULSE_COLORS.textMuted, fontSize: '12px', cursor: 'pointer',
                                whiteSpace: expanded ? 'pre-wrap' : 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                        >
                            {epic.description_excerpt}
                        </div>
                    ) : (
                        <div style={{ color: PULSE_COLORS.textSubtle, fontSize: '12px', fontStyle: 'italic' }}>
                            no description
                        </div>
                    )}

                    {/* AI suggestion + rationale */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <span style={{ color: PULSE_COLORS.textSubtle, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            AI suggests
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ width: '8px', height: '8px', background: sugMeta.color, borderRadius: '50%' }} />
                            <strong style={{ color: PULSE_COLORS.text }}>{sugMeta.label}</strong>
                        </span>
                        {confPct != null && (
                            <span title={`${epic.confidence_label} confidence`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                <span style={{ width: '60px', height: '6px', background: PULSE_COLORS.bgInner, borderRadius: '3px', overflow: 'hidden', display: 'inline-block' }}>
                                    <span style={{ display: 'block', width: `${confPct}%`, height: '100%', background: confColor(epic.confidence_label) }} />
                                </span>
                                <span style={{ color: confColor(epic.confidence_label), fontSize: '11px', fontWeight: 600 }}>{confPct}%</span>
                            </span>
                        )}
                        {epic.rationale && (
                            <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', fontStyle: 'italic' }}>
                                — {epic.rationale}
                            </span>
                        )}
                    </div>
                </div>

                {/* Approve / override controls */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', minWidth: '210px' }}>
                    {approved ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: PULSE_COLORS.success, fontWeight: 600 }}>
                                ✓ {effMeta.label}
                            </span>
                            {epic.approved_by && (
                                <span style={{ color: PULSE_COLORS.textSubtle, fontSize: '10px' }}>
                                    by {epic.approved_by}
                                </span>
                            )}
                            <button
                                onClick={() => onApprove(epic.epic_key, choice)}
                                disabled={saving || choice === epic.category}
                                style={{
                                    marginTop: '2px', background: 'transparent', color: PULSE_COLORS.textMuted,
                                    border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '4px',
                                    padding: '2px 8px', fontSize: '10px',
                                    cursor: saving || choice === epic.category ? 'default' : 'pointer',
                                    opacity: choice === epic.category ? 0.5 : 1,
                                }}
                            >
                                re-approve as {CATEGORY_META[choice]?.label}
                            </button>
                        </div>
                    ) : null}

                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {EPIC_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setChoice(c)}
                                title={CATEGORY_META[c].label}
                                style={{
                                    background: choice === c ? `${CATEGORY_META[c].color}40` : 'transparent',
                                    color: choice === c ? PULSE_COLORS.text : PULSE_COLORS.textMuted,
                                    border: `1px solid ${choice === c ? CATEGORY_META[c].color : PULSE_COLORS.border}`,
                                    borderRadius: '999px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer',
                                }}
                            >
                                {CATEGORY_META[c].icon} {CATEGORY_META[c].label}
                            </button>
                        ))}
                    </div>

                    {!approved && (
                        <button
                            onClick={() => onApprove(epic.epic_key, choice)}
                            disabled={saving}
                            style={{
                                background: overriding ? PULSE_COLORS.warning : PULSE_COLORS.success,
                                color: '#0f172a', border: 'none', borderRadius: '4px',
                                padding: '5px 14px', fontSize: '12px', fontWeight: 700,
                                cursor: saving ? 'wait' : 'pointer',
                            }}
                        >
                            {saving ? 'Saving…' : overriding ? `Approve as ${CATEGORY_META[choice].label}` : 'Approve'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
